"use client";

import { useEffect, useState, useMemo, useRef } from "react";
import AuthenticatedLayout from "../../Layouts/AuthenticatedLayout";
import InfoSection from "../../Components/InfoSection";
import GroupedContainer from "../../Components/GroupedContainer";
import {
  TbSearch,
  TbX,
  TbFilter,
  TbCalendar,
  TbChevronDown,
  TbCheck,
  TbInfoCircle,
  TbCircleCheck,
  TbAlertCircle,
  TbEye,
  TbTrash,
  TbPlus,
} from "react-icons/tb";
import { ChevronLeft, ChevronRight } from "lucide-react";
import SecondaryButton from "../../Components/SecondaryButton";
import axios from "axios";

// Componente Snackbar
function Snackbar({ message, type = "success", onClose, duration = 5000 }) {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(onClose, 300);
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onClose]);

  const bgColor = {
    success: "bg-green-600 border-green-700",
    error: "bg-red-600 border-red-700",
    info: "bg-blue-600 border-blue-700",
    warning: "bg-amber-600 border-amber-700",
  }[type];

  const icon = {
    success: <TbCircleCheck className="w-5 h-5" />,
    error: <TbAlertCircle className="w-5 h-5" />,
    info: <TbInfoCircle className="w-5 h-5" />,
    warning: <TbInfoCircle className="w-5 h-5" />,
  }[type];

  return (
    <div
      className={`fixed bottom-4 right-4 z-50 transform transition-all duration-300 ${
        isVisible ? "translate-x-0 opacity-100" : "translate-x-full opacity-0"
      }`}
    >
      <div
        className={`${bgColor} text-white px-6 py-4 rounded-lg shadow-lg border max-w-sm flex items-start gap-3`}
      >
        <div className="flex-shrink-0 mt-0.5">{icon}</div>
        <div className="flex-1">
          <p className="text-sm font-medium">{message}</p>
        </div>
        <button
          onClick={() => {
            setIsVisible(false);
            setTimeout(onClose, 300);
          }}
          className="flex-shrink-0 text-white/80 hover:text-white ml-2"
        >
          <TbX className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}

/**
 * ✅ FIX: Modal EXTRAÍDA para fora do componente principal
 * Assim ela NÃO é desmontada/montada a cada snackbar (re-render).
 */
function ItemsViewModal({
  viewItemsModalOpen,
  setViewItemsModalOpen,
  currentViewItems,
  setCurrentViewItems,
  currentTransactionForItems,
  setCurrentTransactionForItems,
  carTypes,
  loadingCarTypes,
  deletingVehicleId,
  deleteVehicle,
  showSnackbar,
  transactions,
  setTransactions,
  setFilteredTransactions,
  isSavingVehicles,
  setIsSavingVehicles,
  itemsModalRef,
}) {
  const scrollContainerRef = useRef(null);

  const DEFAULT_NEW_VEHICLE = useMemo(
    () => ({
      car_type_id: "",
      make: "",
      model: "",
      year: "",
      is_operable: true,
    }),
    []
  );

  const [localItems, setLocalItems] = useState([]);
  const [showNewVehicleForm, setShowNewVehicleForm] = useState(false);
  const [newVehicle, setNewVehicle] = useState(DEFAULT_NEW_VEHICLE);
  const [savingNewVehicle, setSavingNewVehicle] = useState(false);

  // ✅ Sync da lista local SEM resetar o formulário
  useEffect(() => {
    if (viewItemsModalOpen) {
      setLocalItems(currentViewItems || []);
    }
  }, [viewItemsModalOpen, currentViewItems]);

  // ✅ Resetar form APENAS quando a modal ABRIR (não quando snackbar disparar)
  useEffect(() => {
    if (viewItemsModalOpen) {
      setShowNewVehicleForm(false);
      setNewVehicle(DEFAULT_NEW_VEHICLE);
    }
  }, [viewItemsModalOpen, DEFAULT_NEW_VEHICLE]);

  if (!viewItemsModalOpen) return null;

  const handleClose = () => {
    setViewItemsModalOpen(false);
    setCurrentViewItems([]);
    setCurrentTransactionForItems(null);
  };

  const handleShowNewVehicleForm = () => {
    setShowNewVehicleForm(true);
    setTimeout(() => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop = 0;
      }
    }, 100);
  };

  const handleCancelNewVehicle = () => {
    setShowNewVehicleForm(false);
    setNewVehicle(DEFAULT_NEW_VEHICLE);
  };

  const handleUpdateNewVehicle = (field, value) => {
    if (field === "car_type_id") {
      setNewVehicle({
        ...newVehicle,
        car_type_id: value,
      });
      return;
    }

    if (field === "year") {
      setNewVehicle({
        ...newVehicle,
        [field]: parseInt(value) || value,
      });
      return;
    }

    if (field === "is_operable") {
      setNewVehicle({
        ...newVehicle,
        [field]: value === "true" || value === true,
      });
      return;
    }

    setNewVehicle({
      ...newVehicle,
      [field]: value,
    });
  };

  const handleSaveNewVehicle = async () => {
    if (!currentTransactionForItems) {
      showSnackbar("No transaction selected", "error");
      return;
    }

    if (!newVehicle.year || !newVehicle.make || !newVehicle.model || !newVehicle.car_type_id) {
      showSnackbar("Please fill all required fields", "error");
      return;
    }

    setSavingNewVehicle(true);
    try {
      const token = localStorage.getItem("token");
      const transactionId = currentTransactionForItems.original_id || currentTransactionForItems.id;

      const payload = {
        car_type_id: parseInt(newVehicle.car_type_id),
        make: newVehicle.make,
        model: newVehicle.model,
        year: parseInt(newVehicle.year),
        is_operable: newVehicle.is_operable,
        external_load_id: transactionId,
      };

      const response = await axios.post("https://new.cajuu.app/api/externalloaditem", payload, {
        headers: {
          "Content-Type": "application/json",
          "Screen-Name": "/Load",
          Authorization: `Bearer ${token}`,
        },
      });

      const createdVehicle = {
        ...payload,
        id: response.data?.id || response.data?.data?.id,
        car_type:
          carTypes.find((t) => t.id === parseInt(newVehicle.car_type_id)) || {
            type: "",
          },
      };

      const updatedItems = [createdVehicle, ...localItems];
      setLocalItems(updatedItems);
      setCurrentViewItems(updatedItems);

      const updatedTransactions = transactions.map((t) => {
        if (t.id === currentTransactionForItems.id) {
          return {
            ...t,
            external_load_items: updatedItems,
            carInfo: updatedItems
              .map((car) => `${car.year || ""} ${car.make || ""} ${car.model || ""}`)
              .join(", "),
          };
        }
        return t;
      });

      setTransactions(updatedTransactions);
      setFilteredTransactions(updatedTransactions);

      setShowNewVehicleForm(false);
      setNewVehicle(DEFAULT_NEW_VEHICLE);

      showSnackbar("Vehicle added successfully!", "success");
    } catch (error) {
      console.error("Error adding new vehicle:", error);
      let errorMessage = "Error adding vehicle";
      if (error.response) {
        errorMessage = error.response.data?.message || error.response.data?.error || errorMessage;
      }
      showSnackbar(errorMessage, "error");
    } finally {
      setSavingNewVehicle(false);
    }
  };

  const handleUpdateExistingVehicle = (index, field, value) => {
    const updatedItems = [...localItems];

    if (field === "car_type_id") {
      const selectedCarType = carTypes.find((type) => type.id === parseInt(value));
      updatedItems[index] = {
        ...updatedItems[index],
        car_type_id: parseInt(value),
        car_type: selectedCarType || { type: "" },
      };
    } else if (field === "year") {
      updatedItems[index] = {
        ...updatedItems[index],
        [field]: parseInt(value) || value,
      };
    } else if (field === "is_operable") {
      updatedItems[index] = {
        ...updatedItems[index],
        [field]: value === "true" || value === true,
      };
    } else {
      updatedItems[index] = {
        ...updatedItems[index],
        [field]: value,
      };
    }

    setLocalItems(updatedItems);
  };

  const handleDeleteVehicleLocal = async (itemId, index) => {
    if (!itemId) {
      const updatedItems = [...localItems];
      updatedItems.splice(index, 1);
      setLocalItems(updatedItems);
      showSnackbar("Unsaved vehicle removed from list", "info");
      return;
    }

    await deleteVehicle(itemId, index);
  };

  const handleSaveAllVehicles = async () => {
    if (!currentTransactionForItems) return;

    setIsSavingVehicles(true);
    try {
      const token = localStorage.getItem("token");
      const transactionId = currentTransactionForItems.original_id || currentTransactionForItems.id;

      const invalidItems = localItems.filter((item) => !item.year || !item.make || !item.model || !item.car_type_id);

      if (invalidItems.length > 0) {
        showSnackbar("Please fill all required fields for all vehicles", "error");
        setIsSavingVehicles(false);
        return;
      }

      const updatePromises = localItems.map(async (item) => {
        const payload = {
          car_type_id: item.car_type_id,
          make: item.make,
          model: item.model,
          year: parseInt(item.year),
          is_operable: item.is_operable,
          external_load_id: transactionId,
        };

        if (item.id) {
          return axios.post(`https://new.cajuu.app/api/externalloaditem/${item.id}`, payload, {
            headers: {
              "Content-Type": "application/json",
              "Screen-Name": "/Load",
              Authorization: `Bearer ${token}`,
            },
          });
        }

        return axios.post("https://new.cajuu.app/api/externalloaditem", payload, {
          headers: {
            "Content-Type": "application/json",
            "Screen-Name": "/Load",
            Authorization: `Bearer ${token}`,
          },
        });
      });

      await Promise.all(updatePromises);

      setCurrentViewItems(localItems);

      const updatedTransactions = transactions.map((t) => {
        if (t.id === currentTransactionForItems.id) {
          return {
            ...t,
            external_load_items: localItems,
            carInfo: localItems.map((car) => `${car.year || ""} ${car.make || ""} ${car.model || ""}`).join(", "),
          };
        }
        return t;
      });

      setTransactions(updatedTransactions);
      setFilteredTransactions(updatedTransactions);

      showSnackbar("All vehicles saved successfully!", "success");

      handleClose();
    } catch (error) {
      console.error("Error saving vehicle changes:", error);

      let errorMessage = "Error saving vehicles";
      if (error.response) {
        errorMessage = error.response.data?.message || error.response.data?.error || errorMessage;
      }

      showSnackbar(errorMessage, "error");
    } finally {
      setIsSavingVehicles(false);
    }
  };

  const canDeleteVehicle = localItems.length > 1;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div
        ref={itemsModalRef}
        className="bg-[#1c1b19] rounded-xl border border-zinc-600 w-full max-w-4xl max-h-[85vh] flex flex-col"
      >
        {/* Cabeçalho */}
        <div className="flex justify-between items-center p-6 border-b border-zinc-600">
          <h2 className="text-xl font-bold text-white">Vehicle Details - Order #{currentTransactionForItems?.order_id}</h2>
          <button onClick={handleClose} className="text-gray-400 hover:text-white p-1" title="Close">
            <TbX className="w-6 h-6" />
          </button>
        </div>

        {/* Conteúdo rolável */}
        <div ref={scrollContainerRef} className="overflow-y-auto flex-1 p-6">
          {/* Botão para mostrar formulário de novo veículo */}
          {!showNewVehicleForm ? (
            <div className="mb-6">
              <button
                onClick={handleShowNewVehicleForm}
                className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors flex items-center gap-2"
              >
                <TbPlus className="w-4 h-4" />
                Add Vehicle
              </button>
              <p className="text-sm text-gray-400 mt-2">
                Click "Add Vehicle" to create a new vehicle form. Changes to existing vehicles are saved when you click
                "Save All Vehicles".
              </p>
            </div>
          ) : (
            /* New Vehicle Form */
            <div className="mb-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-green-500">
                  Add New Vehicle
                  <span className="ml-2 text-sm text-green-400">(Fill the form below and click "Add Vehicle to List")</span>
                </h3>
                <button onClick={handleCancelNewVehicle} className="text-gray-400 hover:text-white p-1">
                  <TbX className="w-5 h-5" />
                </button>
              </div>

              <div className="bg-zinc-800/30 p-4 rounded-lg border border-green-600">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">
                      Year <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={newVehicle.year || ""}
                      onChange={(e) => handleUpdateNewVehicle("year", e.target.value)}
                      required
                      className="w-full px-4 py-2 bg-transparent text-white border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                      placeholder="e.g., 2020"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">
                      Make <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={newVehicle.make || ""}
                      onChange={(e) => handleUpdateNewVehicle("make", e.target.value)}
                      required
                      className="w-full px-4 py-2 bg-transparent text-white border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                      placeholder="e.g., Toyota"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">
                      Model <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={newVehicle.model || ""}
                      onChange={(e) => handleUpdateNewVehicle("model", e.target.value)}
                      required
                      className="w-full px-4 py-2 bg-transparent text-white border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                      placeholder="e.g., Camry"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">
                      Vehicle Type <span className="text-red-500">*</span>
                    </label>
                    {loadingCarTypes ? (
                      <div className="flex items-center gap-2">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-green-500"></div>
                        <span className="text-sm text-gray-400">Loading types...</span>
                      </div>
                    ) : (
                      <select
                        value={newVehicle.car_type_id || ""}
                        onChange={(e) => handleUpdateNewVehicle("car_type_id", e.target.value)}
                        required
                        className="w-full px-4 py-2 bg-transparent text-white border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                      >
                        <option value="" className="bg-[#1c1b19]">
                          Select vehicle type
                        </option>
                        {carTypes.map((type) => (
                          <option key={type.id} value={String(type.id)} className="bg-[#1c1b19]">
                            {type.type || type.name}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">Condition</label>
                    <select
                      value={newVehicle.is_operable ? "true" : "false"}
                      onChange={(e) => handleUpdateNewVehicle("is_operable", e.target.value)}
                      className="w-full px-4 py-2 bg-transparent text-white border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    >
                      <option value="true" className="bg-[#1c1b19]">
                        Operable
                      </option>
                      <option value="false" className="bg-[#1c1b19]">
                        Inoperable
                      </option>
                    </select>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-zinc-600">
                  <button
                    onClick={handleSaveNewVehicle}
                    disabled={savingNewVehicle}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {savingNewVehicle ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Adding...
                      </>
                    ) : (
                      <>
                        <TbPlus className="w-4 h-4" />
                        Add Vehicle to List
                      </>
                    )}
                  </button>
                  <p className="text-xs text-gray-400 mt-2">
                    Click "Add Vehicle to List" to save this vehicle to the database and add it to the list below.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Existing Vehicles List */}
          <div className="space-y-6">
            {localItems.length > 0 ? (
              <>
                <h3 className="text-lg font-semibold text-amber-500 mb-4">Existing Vehicles ({localItems.length})</h3>
                {localItems.map((item, index) => (
                  <div key={item.id || `new-${index}`} className="bg-zinc-800/30 p-4 rounded-lg border border-zinc-600">
                    <div className="flex justify-between items-center mb-4">
                      <h4 className="text-md font-semibold text-white">
                        Vehicle {index + 1}
                        {!item.id && <span className="ml-2 text-sm text-yellow-400">(Not saved yet)</span>}
                      </h4>
                      <button
                        onClick={() => handleDeleteVehicleLocal(item.id, index)}
                        disabled={deletingVehicleId === item.id || !canDeleteVehicle}
                        className="p-1 text-gray-400 hover:text-red-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed relative group"
                        title={!canDeleteVehicle ? "Cannot delete the only vehicle" : "Delete vehicle"}
                      >
                        {deletingVehicleId === item.id ? (
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        ) : (
                          <TbTrash className="w-5 h-5" />
                        )}
                        {!canDeleteVehicle && (
                          <div className="absolute invisible group-hover:visible -top-8 -right-2 bg-gray-900 text-white text-xs px-2 py-1 rounded shadow-lg whitespace-nowrap z-10">
                            At least one vehicle required
                          </div>
                        )}
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-400 mb-1">
                          Year <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={item.year || ""}
                          onChange={(e) => handleUpdateExistingVehicle(index, "year", e.target.value)}
                          className="w-full px-4 py-2 bg-transparent text-white border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-400 mb-1">
                          Make <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={item.make || ""}
                          onChange={(e) => handleUpdateExistingVehicle(index, "make", e.target.value)}
                          className="w-full px-4 py-2 bg-transparent text-white border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-400 mb-1">
                          Model <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={item.model || ""}
                          onChange={(e) => handleUpdateExistingVehicle(index, "model", e.target.value)}
                          className="w-full px-4 py-2 bg-transparent text-white border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-400 mb-1">
                          Vehicle Type <span className="text-red-500">*</span>
                        </label>
                        {loadingCarTypes ? (
                          <div className="flex items-center gap-2">
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-amber-500"></div>
                            <span className="text-sm text-gray-400">Loading types...</span>
                          </div>
                        ) : (
                          <select
                            value={item.car_type_id ? String(item.car_type_id) : ""}
                            onChange={(e) => handleUpdateExistingVehicle(index, "car_type_id", e.target.value)}
                            className="w-full px-4 py-2 bg-transparent text-white border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                          >
                            <option value="" className="bg-[#1c1b19]">
                              Select vehicle type
                            </option>
                            {carTypes.map((type) => (
                              <option key={type.id} value={String(type.id)} className="bg-[#1c1b19]">
                                {type.type || type.name}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-400 mb-1">Condition</label>
                        <select
                          value={item.is_operable ? "true" : "false"}
                          onChange={(e) => handleUpdateExistingVehicle(index, "is_operable", e.target.value)}
                          className="w-full px-4 py-2 bg-transparent text-white border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                        >
                          <option value="true" className="bg-[#1c1b19]">
                            Operable
                          </option>
                          <option value="false" className="bg-[#1c1b19]">
                            Inoperable
                          </option>
                        </select>
                      </div>
                    </div>
                  </div>
                ))}
              </>
            ) : !showNewVehicleForm ? (
              <div className="text-center py-8 border-2 border-dashed border-zinc-600 rounded-lg">
                <div className="text-gray-400 mb-4">
                  <TbInfoCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p className="text-lg mb-2">No vehicles added yet</p>
                  <p className="text-sm">Click the "Add Vehicle" button above to create your first vehicle</p>
                </div>
              </div>
            ) : null}
          </div>
        </div>

        {/* Rodapé */}
        <div className="border-t border-zinc-600 p-6">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-400">
              {localItems.length} vehicle(s) in this order
              {localItems.length === 1 && " (minimum required)"}
              {localItems.some((item) => !item.id) && (
                <span className="ml-2 text-yellow-400">
                  • {localItems.filter((item) => !item.id).length} unsaved vehicle(s)
                </span>
              )}
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={handleClose}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Close
              </button>
              <button
                onClick={handleSaveAllVehicles}
                disabled={isSavingVehicles || localItems.length === 0}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSavingVehicles ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline-block"></div>
                    Saving...
                  </>
                ) : (
                  "Save All Vehicles"
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ExternalLoad() {
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]); // agora = itens da página atual (backend)
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [paginationMeta, setPaginationMeta] = useState({
    total: 0,
    from: 0,
    to: 0,
    per_page: 10,
  });

  const [loading, setLoading] = useState(false);

  // filtros
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // Date range (From/To)
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const [driveSearch, setDriveSearch] = useState("");
  const [selectedDrive, setSelectedDrive] = useState("all");
  const [showDrivesList, setShowDrivesList] = useState(false);

  const [openStatusDropdown, setOpenStatusDropdown] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [currentEditTransaction, setCurrentEditTransaction] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    type: "success",
  });

  // Estados para a modal de veículos
  const [viewItemsModalOpen, setViewItemsModalOpen] = useState(false);
  const [currentViewItems, setCurrentViewItems] = useState([]);
  const [currentTransactionForItems, setCurrentTransactionForItems] = useState(null);
  const [isSavingVehicles, setIsSavingVehicles] = useState(false);
  const [carTypes, setCarTypes] = useState([]);
  const [loadingCarTypes, setLoadingCarTypes] = useState(false);
  const [deletingVehicleId, setDeletingVehicleId] = useState(null);

  const dropdownRef = useRef(null);
  const driveSearchRef = useRef(null);
  const modalRef = useRef(null);
  const itemsModalRef = useRef(null);

  const itemsPerPage = 10;

  // ✅ Normaliza qualquer date string para "YYYY-MM-DD"
  const toDateKey = (dateStr) => {
    if (!dateStr || typeof dateStr !== "string") return "";
    const base = dateStr.split("T")[0];
    return base;
  };

  // Debounce do search (evita chamar API a cada tecla)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 450);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Status options
  const statusOptions = [
    { value: "assigned", label: "Assigned", color: "bg-cyan-500/20 text-cyan-300 border border-cyan-500/40" },
    { value: "pickedUp", label: "Picked Up", color: "bg-blue-500/20 text-blue-300 border border-blue-500/40" },
    { value: "delivered", label: "Delivered", color: "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40" },
    { value: "billed", label: "Billed", color: "bg-yellow-500/20 text-yellow-300 border border-yellow-500/40" },
    { value: "paid", label: "Paid", color: "bg-lime-500/20 text-lime-300 border border-lime-500/40" },
    { value: "canceled", label: "Canceled", color: "bg-red-500/20 text-red-300 border border-red-500/40" },
  ];

  // Mapear load_status_id para status do sistema
  const mapLoadStatusToSystemStatus = (loadStatusId) => {
    switch (parseInt(loadStatusId)) {
      case 2:
        return "assigned";
      case 4:
        return "pickedUp";
      case 5:
        return "delivered";
      case 7:
        return "billed";
      case 8:
        return "paid";
      case 9:
        return "canceled";
      default:
        return "assigned";
    }
  };

  // Mapear status do sistema para load_status_id
  const mapSystemStatusToLoadStatus = (systemStatus) => {
    switch (systemStatus) {
      case "assigned":
        return 2;
      case "pickedUp":
        return 4;
      case "delivered":
        return 5;
      case "billed":
        return 7;
      case "paid":
        return 8;
      case "canceled":
        return 9;
      default:
        return 2;
    }
  };

  // Payment type options
  const paymentTypeOptions = [
    { value: "COD", label: "COD" },
    { value: "COP", label: "COP" },
    { value: "2 BD", label: "2 BD" },
    { value: "5 BD", label: "5 BD" },
    { value: "7 BD", label: "7 BD" },
    { value: "15 BD", label: "15 BD" },
    { value: "30 BD", label: "30 BD" },
  ];

  // Payment method options
  const paymentMethodOptions = [
    { value: "ACH", label: "ACH" },
    { value: "Check", label: "Check" },
    { value: "Zelle", label: "Zelle" },
    { value: "Venmo", label: "Venmo" },
    { value: "Cashapp", label: "Cashapp" },
    { value: "Uship", label: "Uship" },
    { value: "Cash", label: "Cash" },
    { value: "PayPal", label: "PayPal" },
    { value: "Credit card", label: "Credit card" },
    { value: "Debit", label: "Debit" },
    { value: "Comcheck", label: "Comcheck" },
    { value: "Echeck", label: "Echeck" },
    { value: "Electronic Check", label: "Electronic Check" },
  ];

  // Load status options
  const loadStatusOptions = [
    { value: 2, label: "Assigned" },
    { value: 4, label: "Picked Up" },
    { value: 5, label: "Delivered" },
    { value: 7, label: "Billed" },
    { value: 8, label: "Paid" },
    { value: 9, label: "Canceled" },
  ];

  // Função para mostrar snackbar
  const showSnackbar = (message, type = "success") => {
    setSnackbar({
      open: true,
      message,
      type,
    });
  };

  // Função para fechar snackbar
  const closeSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const unwrapApiList = (result) => {
    if (!result) return [];
    if (Array.isArray(result)) return result;
    if (Array.isArray(result?.data?.data)) return result.data.data;
    if (Array.isArray(result?.data)) return result.data;
    return [];
  };

  const driverLabelFromDriverApi = (d) => {
    const u = d?.user;
    const first = u?.first_name || "";
    const last = u?.last_name || "";
    const full = `${first} ${last}`.trim();
    if (full) return full;
    if (u?.email) return u.email;
    return `Driver #${d?.id ?? ""}`;
  };

  const fetchDrivers = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get("https://new.cajuu.app/api/driver", {
        headers: {
          "Screen-Name": "/Load",
          Authorization: `Bearer ${token}`,
        },
      });

      const list = unwrapApiList(res?.data);
      const mapped = list.map((d) => ({
        id: d.id,
        name: driverLabelFromDriverApi(d),
      }));

      mapped.sort((a, b) => String(a.name).localeCompare(String(b.name)));
      setDrivers(mapped);
    } catch (error) {
      console.error("Error fetching drivers:", error);
      setDrivers([]);
    }
  };

  // Função para buscar tipos de carro
  const fetchCarTypes = async () => {
    setLoadingCarTypes(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get("https://new.cajuu.app/api/cartype", {
        headers: {
          "Screen-Name": "/Load",
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data && response.data.data) {
        setCarTypes(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching car types:", error);
      showSnackbar("Erro ao carregar tipos de veículo", "error");
    } finally {
      setLoadingCarTypes(false);
    }
  };

  // ✅ Backend-driven fetch (filtros + paginação no controller)
  const fetchTransactions = async (pageToFetch = currentPage) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");

      // date range: normaliza e auto-corrige invertido
      const fromKeyRaw = toDateKey(dateFrom);
      const toKeyRaw = toDateKey(dateTo);

      let fromKey = fromKeyRaw;
      let toKey = toKeyRaw;

      if (fromKey && toKey && fromKey > toKey) {
        const tmp = fromKey;
        fromKey = toKey;
        toKey = tmp;
      }

      const params = {
        page: pageToFetch,
        per_page: itemsPerPage,
        sort_by: "created_at",
        sort_direction: "desc",
      };

      const q = (debouncedSearchQuery || "").trim();
      if (q) params.order_id = q;

      if (statusFilter !== "all") {
        params.status = mapSystemStatusToLoadStatus(statusFilter); // load_status_id
      }

      if (selectedDrive !== "all") {
        params.driver_id = parseInt(selectedDrive);
      }

      if (fromKey) params.date_from = fromKey;
      if (toKey) params.date_to = toKey;

      const response = await axios.get("https://new.cajuu.app/api/externalload", {
        params,
        headers: {
          "Screen-Name": "/Load",
          Authorization: `Bearer ${token}`,
        },
      });

      const result = response.data;
      const paginator = result?.data || {};
      const list = Array.isArray(paginator?.data) ? paginator.data : [];

      // Se o usuário ficou numa página acima do last_page (ex: deletou o último item),
      // ajusta automaticamente para o last_page.
      const apiLastPage = parseInt(paginator?.last_page || 1);
      const apiCurrentPage = parseInt(paginator?.current_page || pageToFetch);

      setTotalPages(apiLastPage || 1);
      setPaginationMeta({
        total: parseInt(paginator?.total || 0),
        from: parseInt(paginator?.from || 0) || 0,
        to: parseInt(paginator?.to || 0) || 0,
        per_page: parseInt(paginator?.per_page || itemsPerPage) || itemsPerPage,
      });

      if (apiLastPage > 0 && pageToFetch > apiLastPage) {
        setCurrentPage(apiLastPage);
      } else {
        setCurrentPage(apiCurrentPage);
      }

      const mappedTransactions = list.map((item) => {
        const driverName =
          item.driver && item.driver.user
            ? `${item.driver.user.first_name} ${item.driver.user.last_name}`
            : drivers.find((d) => d.id === item.driver_id)?.name || `Driver ${item.driver_id}`;

        const mappedItems = (item.external_load_items || []).map((vehicle) => ({
          id: vehicle.id,
          external_load_id: vehicle.external_load_id,
          car_type_id: vehicle.car_type_id,
          make: vehicle.make,
          model: vehicle.model,
          year: vehicle.year,
          is_operable: vehicle.is_operable,
          partner_company_id: vehicle.partner_company_id,
          car_type: vehicle.car_type || { type: "" },
        }));

        const pickupDateKey = toDateKey(item.pickup_date) || toDateKey(item.created_at);
        const dropoffDateKey = toDateKey(item.dropoff_date) || "";

        return {
          id: item.id.toString(),
          order_id: item.order_id,
          date: pickupDateKey,
          description: item.order_id,
          amount: parseFloat(item.payment_rate) || 0,
          broker_fee: parseFloat(item.broker_fee) || 0,
          type: "income",
          status: mapLoadStatusToSystemStatus(item.load_status_id),
          payment_type: "income",
          payment_method: item.payment_method || "Wire Transfer",
          payment_rate: parseFloat(item.payment_rate) || 0,
          reference: item.order_id,
          carInfo:
            mappedItems.length > 0
              ? mappedItems.map((car) => `${car.year || ""} ${car.make || ""} ${car.model || ""}`).join(", ")
              : "No vehicle info",
          driver_id: item.driver_id,
          driver_name: driverName,
          load_status_id: item.load_status_id,
          loadboard: item.loadboard,
          pickup_city: item.pickup_city,
          pickup_state: item.pickup_state,
          pickup_zipcode: item.pickup_zipcode,
          delivery_city: item.delivery_city,
          delivery_state: item.delivery_state,
          delivery_zipcode: item.delivery_zipcode,
          pickup_date: pickupDateKey,
          dropoff_date: dropoffDateKey,
          original_id: item.id,
          external_load_items: mappedItems,
          partner_company: item.partner_company,
        };
      });

      setTransactions(mappedTransactions);
      setFilteredTransactions(mappedTransactions); // agora é só espelho da página atual
    } catch (error) {
      console.error("Error fetching transactions:", error);
      setTransactions([]);
      setFilteredTransactions([]);
      setTotalPages(1);
      setPaginationMeta({ total: 0, from: 0, to: 0, per_page: itemsPerPage });
      showSnackbar("Erro ao carregar transações", "error");
    } finally {
      setLoading(false);
    }
  };

  // Função para deletar transação (depois refaz fetch da página atual)
  const handleDelete = async (transactionId) => {
    const transaction = transactions.find((t) => t.id === transactionId);
    if (!transaction) return;

    if (!window.confirm(`Are you sure you want to delete transaction #${transaction.order_id}? This action cannot be undone.`)) {
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const idToDelete = transaction.original_id || transactionId;

      await axios.delete(`https://new.cajuu.app/api/externalload/${idToDelete}`, {
        headers: {
          "Content-Type": "application/json",
          "Screen-Name": "/Load",
          Authorization: `Bearer ${token}`,
        },
      });

      showSnackbar("Transação deletada com sucesso!", "success");
      await fetchTransactions(currentPage);
    } catch (error) {
      console.error("Error deleting transaction:", error);

      let errorMessage = "Erro ao deletar transação";
      if (error.response) {
        errorMessage = error.response.data?.message || error.response.data?.error || errorMessage;
      }

      showSnackbar(errorMessage, "error");
    }
  };

  // Função para abrir a modal de visualização de veículos
  const handleViewItems = (transaction) => {
    const mappedItems = (transaction.external_load_items || []).map((item) => ({
      id: item.id,
      external_load_id: item.external_load_id,
      car_type_id: item.car_type_id || "",
      make: item.make || "",
      model: item.model || "",
      year: item.year || "",
      is_operable: item.is_operable || false,
      partner_company_id: item.partner_company_id,
      car_type: item.car_type || { type: "" },
    }));

    setCurrentViewItems(mappedItems);
    setCurrentTransactionForItems(transaction);
    setViewItemsModalOpen(true);
  };

  // Função para deletar um veículo individual
  const deleteVehicle = async (itemId, index) => {
    if (!itemId) {
      const updatedItems = [...currentViewItems];
      updatedItems.splice(index, 1);
      setCurrentViewItems(updatedItems);
      return;
    }

    if (!window.confirm("Are you sure you want to delete this vehicle? This action cannot be undone.")) {
      return;
    }

    setDeletingVehicleId(itemId);
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`https://new.cajuu.app/api/externalloaditem/${itemId}`, {
        headers: {
          "Content-Type": "application/json",
          "Screen-Name": "/Load",
          Authorization: `Bearer ${token}`,
        },
      });

      const updatedItems = [...currentViewItems];
      updatedItems.splice(index, 1);
      setCurrentViewItems(updatedItems);

      showSnackbar("Vehicle deleted successfully!", "success");
    } catch (error) {
      console.error("Error deleting vehicle:", error);

      let errorMessage = "Error deleting vehicle";
      if (error.response) {
        errorMessage = error.response.data?.message || error.response.data?.error || errorMessage;
      }

      showSnackbar(errorMessage, "error");
    } finally {
      setDeletingVehicleId(null);
    }
  };

  // Efeito para resetar o filtro quando o campo estiver vazio
  useEffect(() => {
    if (driveSearch.trim() === "" && selectedDrive !== "all") {
      setSelectedDrive("all");
      setCurrentPage(1);
    }
  }, [driveSearch, selectedDrive]);

  // Efeito para buscar dados iniciais
  useEffect(() => {
    fetchDrivers();
    fetchCarTypes();
  }, []);

  // ✅ Efeito principal: sempre que filtros/página mudar -> backend
  useEffect(() => {
    fetchTransactions(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, debouncedSearchQuery, statusFilter, dateFrom, dateTo, selectedDrive]);

  // Efeito para fechar a lista ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (driveSearchRef.current && !driveSearchRef.current.contains(event.target)) {
        setShowDrivesList(false);
        if (driveSearch.trim() === "" && selectedDrive !== "all") {
          setTimeout(() => {
            setSelectedDrive("all");
            setCurrentPage(1);
          }, 10);
        }
      }

      if (openStatusDropdown && !event.target.closest(".status-dropdown-container")) {
        setOpenStatusDropdown(null);
      }

      if (modalRef.current && !modalRef.current.contains(event.target) && editModalOpen) {
        setEditModalOpen(false);
      }

      if (itemsModalRef.current && !itemsModalRef.current.contains(event.target) && viewItemsModalOpen) {
        setViewItemsModalOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [driveSearch, selectedDrive, openStatusDropdown, editModalOpen, viewItemsModalOpen]);

  // Filtrar drives com base na busca (apenas UI do dropdown)
  const filteredDrives = useMemo(() => {
    return drivers.filter((drive) => drive.name.toLowerCase().includes(driveSearch.toLowerCase()));
  }, [driveSearch, drivers]);

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const clearSearch = () => {
    setSearchQuery("");
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSearchQuery("");
    setStatusFilter("all");

    setDateFrom("");
    setDateTo("");

    setDriveSearch("");
    setSelectedDrive("all");
    setShowDrivesList(false);

    setCurrentPage(1);
    showSnackbar("Filters clean", "info");
  };

  const handleEdit = (transactionId) => {
    const transaction = transactions.find((t) => t.id === transactionId);
    setCurrentEditTransaction({ ...transaction });
    setEditModalOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!currentEditTransaction) return;

    setIsSaving(true);
    try {
      const token = localStorage.getItem("token");
      const transactionId = currentEditTransaction.original_id || currentEditTransaction.id;

      const payload = {
        driver_id: currentEditTransaction.driver_id ? parseInt(currentEditTransaction.driver_id) : null,
        load_status_id: currentEditTransaction.load_status_id ? parseInt(currentEditTransaction.load_status_id) : 4,
        loadboard: currentEditTransaction.loadboard || "",
        order_id: currentEditTransaction.order_id || "",
        pickup_city: currentEditTransaction.pickup_city || "",
        pickup_state: currentEditTransaction.pickup_state || "",
        pickup_zipcode: currentEditTransaction.pickup_zipcode || "",
        delivery_city: currentEditTransaction.delivery_city || "",
        delivery_state: currentEditTransaction.delivery_state || "",
        delivery_zipcode: currentEditTransaction.delivery_zipcode || "",
        payment_rate: parseFloat(currentEditTransaction.payment_rate) || 0,
        payment_type: currentEditTransaction.payment_type || "income",
        payment_method: currentEditTransaction.payment_method || "wire_transfer",
        broker_fee: parseFloat(currentEditTransaction.broker_fee) || 0,
        pickup_date: currentEditTransaction.pickup_date || "",
        dropoff_date: currentEditTransaction.dropoff_date || "",
      };

      const response = await axios.post(`https://new.cajuu.app/api/externalload/${transactionId}`, payload, {
        headers: {
          "Content-Type": "application/json",
          "Screen-Name": "/Load",
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 200 || response.status === 201) {
        setEditModalOpen(false);
        setCurrentEditTransaction(null);

        showSnackbar("Transação atualizada com sucesso!", "success");

        // ✅ refaz fetch da página atual (mantém filtros/página)
        await fetchTransactions(currentPage);
      } else {
        throw new Error("Falha ao atualizar a transação");
      }
    } catch (error) {
      console.error("Erro ao atualizar transação:", error);

      let errorMessage = "Erro ao atualizar a transação";

      if (error.response) {
        errorMessage = error.response.data?.message || error.response.data?.error || "Erro desconhecido da API";
      } else if (error.request) {
        errorMessage = "Erro de rede. Verifique sua conexão.";
      } else {
        errorMessage = "Erro na configuração da requisição.";
      }

      showSnackbar(errorMessage, "error");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDriveSelect = (driveId, driveName) => {
    setSelectedDrive(driveId);
    setDriveSearch(driveName);
    setShowDrivesList(false);
    setCurrentPage(1);
  };

  const clearDriveFilter = () => {
    setSelectedDrive("all");
    setDriveSearch("");
    setShowDrivesList(false);
    setCurrentPage(1);
  };

  const handleDriveBlur = () => {
    setTimeout(() => {
      setShowDrivesList(false);
    }, 200);
  };

  const toggleStatusDropdown = (transactionId) => {
    setOpenStatusDropdown(openStatusDropdown === transactionId ? null : transactionId);
  };

  const updateTransactionStatus = async (transactionId, newStatus) => {
    try {
      const updatedTransactions = transactions.map((t) => {
        if (t.id === transactionId) {
          return {
            ...t,
            status: newStatus,
            load_status_id: mapSystemStatusToLoadStatus(newStatus),
          };
        }
        return t;
      });

      setTransactions(updatedTransactions);
      setOpenStatusDropdown(null);

      showSnackbar(`Status atualizado para: ${newStatus}`, "success");
    } catch (error) {
      console.error("Error updating status:", error);
      showSnackbar("Erro ao atualizar status", "error");
    }
  };

  // ✅ FIX: evita "voltar um dia" por timezone (parse local)
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const key = toDateKey(dateString);
    const parts = key.split("-");
    if (parts.length !== 3) return "N/A";
    const y = Number(parts[0]);
    const m = Number(parts[1]) - 1;
    const d = Number(parts[2]);
    const dt = new Date(y, m, d); // local time
    const options = { year: "numeric", month: "short", day: "numeric" };
    return dt.toLocaleDateString("en-US", options);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const getStatusStyle = (statusValue) => {
    const statusOption = statusOptions.find((opt) => opt.value === statusValue);
    if (!statusOption) {
      return "bg-gray-500/10 text-gray-500 border border-gray-500/20";
    }
    return statusOption.color;
  };

  const getStatusLabel = (statusValue) => {
    const statusOption = statusOptions.find((opt) => opt.value === statusValue);
    return statusOption ? statusOption.label : "Unknown";
  };

  // Paginação (backend)
  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const nextPage = () => setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  const prevPage = () => setCurrentPage((prev) => Math.max(prev - 1, 1));

  const currentTransactions = filteredTransactions; // página atual do backend

  return (
    <AuthenticatedLayout>
      <div className="text-white text-base sm:text-lg md:text-xl lg:text-2xl p-4 sm:p-6 md:p-8">
        <GroupedContainer>
          <InfoSection title="EXTERNAL LOADS" description="External loads overview" />
        </GroupedContainer>

        {/* Filtros */}
        <div className="bg-[#1c1b19] p-4 sm:p-6 rounded-xl shadow-sm text-white border border-zinc-600 mt-4 mb-4">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg sm:text-xl font-bold">Filters</h2>
            <button onClick={clearFilters} className="text-sm text-gray-400 hover:text-white flex items-center gap-1">
              <TbX className="w-4 h-4" /> Clear Filters
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Busca geral */}
            <div className="relative">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <TbSearch className="text-white text-lg" />
              </div>
              <input
                type="text"
                placeholder="Order ID"
                value={searchQuery}
                onChange={handleSearch}
                className="w-full pl-10 pr-10 bg-transparent placeholder:text-white py-2 sm:py-3 text-sm border text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
              />
              {searchQuery && (
                <button
                  onClick={clearSearch}
                  className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-gray-600"
                >
                  <TbX className="text-white" />
                </button>
              )}
            </div>

            {/* Filtro por drive com dropdown customizado */}
            <div className="relative" ref={driveSearchRef}>
              <div className="relative">
                <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                  <TbSearch className="text-white text-lg" />
                </div>
                <input
                  type="text"
                  placeholder="Search drivers..."
                  value={driveSearch}
                  onChange={(e) => {
                    setDriveSearch(e.target.value);
                    if (e.target.value.trim() !== "") {
                      setShowDrivesList(true);
                    }
                  }}
                  onFocus={() => setShowDrivesList(true)}
                  onBlur={handleDriveBlur}
                  className="w-full pl-10 pr-10 bg-transparent placeholder:text-white py-2 sm:py-3 text-sm border text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                />
                {(driveSearch || selectedDrive !== "all") && (
                  <button
                    onClick={clearDriveFilter}
                    className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-white"
                  >
                    <TbX className="w-4 h-4" />
                  </button>
                )}
              </div>

              {showDrivesList && (
                <div className="absolute z-10 mt-2 w-full bg-[#1c1b19] border border-zinc-600 rounded-lg overflow-hidden shadow-lg">
                  <div className="max-h-48 overflow-y-auto">
                    <div
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => handleDriveSelect("all", "")}
                      className="px-4 py-3 cursor-pointer hover:bg-zinc-800/50 transition-colors flex items-center justify-between border-b border-zinc-600"
                    >
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <span className="px-2 py-1 text-center text-white text-sm">All Drivers</span>
                      </div>
                      {selectedDrive === "all" && <TbCheck className="text-amber-500 flex-shrink-0 ml-2" />}
                    </div>

                    {filteredDrives.length > 0 ? (
                      <>
                        {filteredDrives.map((d) => (
                          <div
                            key={d.id}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => handleDriveSelect(String(d.id), d.name)}
                            className="px-4 py-3 cursor-pointer hover:bg-zinc-800/50 transition-colors flex items-center justify-between border-b border-zinc-600 last:border-b-0"
                          >
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                              <span className="px-2 py-1 text-center text-white text-sm" title={d.name}>
                                {d.name}
                              </span>
                            </div>
                            {selectedDrive === String(d.id) && <TbCheck className="text-amber-500 flex-shrink-0 ml-2" />}
                          </div>
                        ))}
                        <div className="px-4 py-2 text-xs text-gray-400 border-t border-zinc-600 bg-zinc-900/50">
                          {driveSearch ? `Found ${filteredDrives.length} of ${drivers.length} drivers` : `Showing all ${filteredDrives.length} drivers`}
                        </div>
                      </>
                    ) : (
                      <div className="px-4 py-3 text-center text-gray-400 text-sm">No drivers found</div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Filtro por status */}
            <div className="relative">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <TbFilter className="text-amber-500 text-lg" />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full pl-10 pr-12 bg-transparent text-white py-2 sm:py-3 text-sm border rounded-lg 
                         focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all 
                         appearance-none"
              >
                <option value="all" className="bg-[#1c1b19]">
                  All Status
                </option>
                {statusOptions.map((option) => (
                  <option key={option.value} value={option.value} className="bg-[#1c1b19]">
                    {option.label}
                  </option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                <TbChevronDown className="text-white text-lg" />
              </div>
            </div>

            {/* Date range (From / To) */}
            <div className="grid grid-cols-2 gap-2">
              {/* From */}
              <div className="relative">
                <span className="absolute -top-2 left-3 bg-[#1c1b19] px-1 text-xs text-gray-400">From</span>
                <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                  <TbCalendar className="text-blue-500 text-lg" />
                </div>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => {
                    setDateFrom(e.target.value);
                    setCurrentPage(1);
                  }}
                  onFocus={(e) => e.target.showPicker?.()}
                  style={{ colorScheme: "dark" }}
                  className="w-full pl-10 pr-10 bg-transparent text-white py-2 sm:py-3 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                />
              </div>

              {/* To */}
              <div className="relative">
                <span className="absolute -top-2 left-3 bg-[#1c1b19] px-1 text-xs text-gray-400">To</span>
                <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                  <TbCalendar className="text-blue-500 text-lg" />
                </div>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => {
                    setDateTo(e.target.value);
                    setCurrentPage(1);
                  }}
                  onFocus={(e) => e.target.showPicker?.()}
                  style={{ colorScheme: "dark" }}
                  className="w-full pl-10 pr-10 bg-transparent text-white py-2 sm:py-3 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Tabela de transações */}
        <div className="mt-6 overflow-x-auto rounded-xl border border-zinc-600">
          <table className="min-w-full divide-y divide-zinc-600">
            <thead className="bg-[#1c1b19]">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Order ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Car</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Payment Rate</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Broker Fee</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Drive</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-[#1c1b19]/50 divide-y divide-zinc-600">
              {loading ? (
                <tr>
                  <td colSpan="8" className="px-6 py-8 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
                    </div>
                    <p className="mt-2 text-gray-400">Loading transactions...</p>
                  </td>
                </tr>
              ) : currentTransactions.length > 0 ? (
                currentTransactions.map((transaction) => (
                  <tr key={transaction.id} className="hover:bg-zinc-800/30 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-white">{transaction.order_id}</td>

                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">{formatDate(transaction.pickup_date)}</td>

                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                      <div className="flex items-center gap-2">
                        <div className="group relative flex-1">
                          <div className="flex items-center gap-2">
                            <span>
                              {transaction.external_load_items?.length > 0
                                ? `${transaction.external_load_items[0].year || ""} ${transaction.external_load_items[0].make || ""} ${transaction.external_load_items[0].model || ""}`
                                : "No vehicle info"}
                            </span>

                            {transaction.external_load_items?.length > 1 && (
                              <span className="bg-green-500 text-white text-xs font-medium px-2 py-1 rounded-full">
                                +{transaction.external_load_items.length - 1}
                              </span>
                            )}

                            <button
                              onClick={() => handleViewItems(transaction)}
                              className="p-1 text-gray-400 hover:text-amber-500 hover:bg-zinc-700/50 rounded-lg transition-colors"
                              title="View vehicle details"
                            >
                              <TbEye className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </td>

                    <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${transaction.type === "income" ? "text-green-500" : "text-red-500"}`}>
                      {formatCurrency(transaction.amount)}
                    </td>

                    <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium text-red-500`}>
                      {formatCurrency(transaction.broker_fee || 0)}
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-300">{transaction.driver_name || "N/A"}</span>
                      </div>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-3 py-1 text-xs rounded-full ${getStatusStyle(transaction.status)}`}>
                        {getStatusLabel(transaction.status)}
                      </span>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleEdit(transaction.id)}
                          className="p-2 text-gray-400 hover:text-amber-500 hover:bg-zinc-700/50 rounded-lg transition-colors"
                          title="Edit transaction"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                            />
                          </svg>
                        </button>

                        <button
                          onClick={() => handleDelete(transaction.id)}
                          className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-500/20 rounded-lg transition-colors"
                          title="Delete transaction"
                        >
                          <TbTrash className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="px-6 py-8 text-center text-gray-400">
                    No transactions found matching your filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Paginação (backend) */}
        {paginationMeta.total > 0 && (
          <>
            <div className="flex items-center justify-center space-x-2 mt-6">
              <SecondaryButton
                variant="outline"
                size="sm"
                onClick={prevPage}
                disabled={currentPage === 1}
                className="text-white border-white hover:bg-white/10"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Previous
              </SecondaryButton>

              <div className="flex items-center space-x-1">
                {(() => {
                  let startPage = Math.max(1, currentPage - 2);
                  const endPage = Math.min(totalPages, startPage + 4);

                  if (endPage - startPage < 4 && startPage > 1) {
                    startPage = Math.max(1, endPage - 4);
                  }

                  const pageNumbers = [];
                  for (let i = startPage; i <= endPage; i++) {
                    pageNumbers.push(
                      <SecondaryButton
                        key={i}
                        size="sm"
                        onClick={() => paginate(i)}
                        className={
                          currentPage === i
                            ? "bg-amber-600 text-black hover:bg-white/90"
                            : "text-black border-white hover:bg-white/10"
                        }
                      >
                        {i}
                      </SecondaryButton>
                    );
                  }
                  return pageNumbers;
                })()}
              </div>

              <SecondaryButton
                variant="outline"
                size="sm"
                onClick={nextPage}
                disabled={currentPage === totalPages}
                className="text-white border-white hover:bg-white/10"
              >
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </SecondaryButton>
            </div>

            <div className="text-center text-sm text-white/70 mt-2">
              Showing {paginationMeta.from || 0}-{paginationMeta.to || 0} of {paginationMeta.total || 0} transactions
            </div>
          </>
        )}
      </div>

      {/* Modal de Edição */}
      {editModalOpen && currentEditTransaction && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div ref={modalRef} className="bg-[#1c1b19] rounded-xl border border-zinc-600 max-w-4xl w-full max-h-[85vh] flex flex-col">
            <div className="flex justify-between items-center p-6 border-b border-zinc-600">
              <h2 className="text-xl font-bold text-white">Edit Transaction id: {currentEditTransaction.id}</h2>
              <button
                onClick={() => {
                  setEditModalOpen(false);
                  setCurrentEditTransaction(null);
                }}
                className="text-gray-400 hover:text-white"
              >
                <TbX className="w-6 h-6" />
              </button>
            </div>

            <div className="overflow-y-auto flex-1 p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Order Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-amber-500 mb-2">Order Information</h3>

                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">
                      Order ID <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={currentEditTransaction.order_id || ""}
                      onChange={(e) =>
                        setCurrentEditTransaction({
                          ...currentEditTransaction,
                          order_id: e.target.value,
                        })
                      }
                      required
                      className="w-full px-4 py-2 bg-transparent text-white border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">
                      Load Status <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={currentEditTransaction.load_status_id || 4}
                      onChange={(e) =>
                        setCurrentEditTransaction({
                          ...currentEditTransaction,
                          load_status_id: parseInt(e.target.value),
                        })
                      }
                      required
                      className="w-full px-4 py-2 bg-transparent text-white border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                    >
                      <option value="" className="bg-[#1c1b19]">
                        Select Load Status
                      </option>
                      {loadStatusOptions.map((option) => (
                        <option key={option.value} value={option.value} className="bg-[#1c1b19]">
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">
                      Loadboard <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={currentEditTransaction.loadboard || ""}
                      onChange={(e) =>
                        setCurrentEditTransaction({
                          ...currentEditTransaction,
                          loadboard: e.target.value,
                        })
                      }
                      required
                      className="w-full px-4 py-2 bg-transparent text-white border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">Driver</label>
                    <select
                      value={currentEditTransaction.driver_id || ""}
                      onChange={(e) =>
                        setCurrentEditTransaction({
                          ...currentEditTransaction,
                          driver_id: e.target.value ? parseInt(e.target.value) : null,
                        })
                      }
                      className="w-full px-4 py-2 bg-transparent text-white border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                    >
                      <option value="" className="bg-[#1c1b19]">
                        Select Driver
                      </option>
                      {drivers.map((drive) => (
                        <option key={drive.id} value={drive.id} className="bg-[#1c1b19]">
                          {drive.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Pickup Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-blue-500 mb-2">Pickup Information</h3>

                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">Pickup City</label>
                    <input
                      type="text"
                      value={currentEditTransaction.pickup_city || ""}
                      onChange={(e) => setCurrentEditTransaction({ ...currentEditTransaction, pickup_city: e.target.value })}
                      className="w-full px-4 py-2 bg-transparent text-white border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">Pickup State</label>
                    <input
                      type="text"
                      value={currentEditTransaction.pickup_state || ""}
                      onChange={(e) =>
                        setCurrentEditTransaction({
                          ...currentEditTransaction,
                          pickup_state: e.target.value,
                        })
                      }
                      className="w-full px-4 py-2 bg-transparent text-white border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 uppercase"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">Pickup Zipcode</label>
                    <input
                      type="text"
                      value={currentEditTransaction.pickup_zipcode || ""}
                      onChange={(e) => setCurrentEditTransaction({ ...currentEditTransaction, pickup_zipcode: e.target.value })}
                      className="w-full px-4 py-2 bg-transparent text-white border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">Pickup Date</label>
                    <input
                      type="date"
                      value={currentEditTransaction.pickup_date || ""}
                      onChange={(e) =>
                        setCurrentEditTransaction({
                          ...currentEditTransaction,
                          pickup_date: e.target.value,
                          date: e.target.value,
                        })
                      }
                      style={{ colorScheme: "dark" }}
                      className="w-full px-4 py-2 bg-transparent text-white border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                    />
                  </div>
                </div>

                {/* Delivery Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-green-500 mb-2">Delivery Information</h3>

                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">Delivery City</label>
                    <input
                      type="text"
                      value={currentEditTransaction.delivery_city || ""}
                      onChange={(e) => setCurrentEditTransaction({ ...currentEditTransaction, delivery_city: e.target.value })}
                      className="w-full px-4 py-2 bg-transparent text-white border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">Delivery State</label>
                    <input
                      type="text"
                      value={currentEditTransaction.delivery_state || ""}
                      onChange={(e) => setCurrentEditTransaction({ ...currentEditTransaction, delivery_state: e.target.value })}
                      className="w-full px-4 py-2 bg-transparent text-white border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">Delivery Zipcode</label>
                    <input
                      type="text"
                      value={currentEditTransaction.delivery_zipcode || ""}
                      onChange={(e) => setCurrentEditTransaction({ ...currentEditTransaction, delivery_zipcode: e.target.value })}
                      className="w-full px-4 py-2 bg-transparent text-white border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">Dropoff Date</label>
                    <input
                      type="date"
                      value={currentEditTransaction.dropoff_date || ""}
                      onChange={(e) => setCurrentEditTransaction({ ...currentEditTransaction, dropoff_date: e.target.value })}
                      style={{ colorScheme: "dark" }}
                      className="w-full px-4 py-2 bg-transparent text-white border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                    />
                  </div>
                </div>

                {/* Payment Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-purple-500 mb-2">Payment Information</h3>

                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">Payment Type</label>
                    <select
                      value={currentEditTransaction.payment_type || "income"}
                      onChange={(e) => setCurrentEditTransaction({ ...currentEditTransaction, payment_type: e.target.value, type: e.target.value })}
                      className="w-full px-4 py-2 bg-transparent text-white border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                    >
                      {paymentTypeOptions.map((option) => (
                        <option key={option.value} value={option.value} className="bg-[#1c1b19]">
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">Payment Method</label>
                    <select
                      value={currentEditTransaction.payment_method || "wire_transfer"}
                      onChange={(e) => setCurrentEditTransaction({ ...currentEditTransaction, payment_method: e.target.value })}
                      className="w-full px-4 py-2 bg-transparent text-white border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                    >
                      {paymentMethodOptions.map((option) => (
                        <option key={option.value} value={option.value} className="bg-[#1c1b19]">
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">Payment Rate ($)</label>
                    <input
                      type="text"
                      value={currentEditTransaction.payment_rate || currentEditTransaction.amount || 0}
                      onChange={(e) =>
                        setCurrentEditTransaction({
                          ...currentEditTransaction,
                          payment_rate: parseFloat(e.target.value) || 0,
                          amount: parseFloat(e.target.value) || 0,
                        })
                      }
                      className="w-full px-4 py-2 bg-transparent text-white border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">Broker Fee ($)</label>
                    <input
                      type="text"
                      value={currentEditTransaction.broker_fee || 0}
                      onChange={(e) => setCurrentEditTransaction({ ...currentEditTransaction, broker_fee: parseFloat(e.target.value) || 0 })}
                      className="w-full px-4 py-2 bg-transparent text-white border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="border-t border-zinc-600 p-6">
              <div className="flex justify-end space-x-3">
                <SecondaryButton
                  variant="outline"
                  onClick={() => {
                    setEditModalOpen(false);
                    setCurrentEditTransaction(null);
                  }}
                  className="text-white border-white hover:bg-white/10"
                >
                  Cancel
                </SecondaryButton>
                <SecondaryButton onClick={handleSaveEdit} className="bg-amber-600 text-black hover:bg-amber-700" disabled={isSaving}>
                  {isSaving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black mr-2"></div>
                      Saving...
                    </>
                  ) : (
                    "Salvar Alterações"
                  )}
                </SecondaryButton>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Visualização de Veículos */}
      <ItemsViewModal
        viewItemsModalOpen={viewItemsModalOpen}
        setViewItemsModalOpen={setViewItemsModalOpen}
        currentViewItems={currentViewItems}
        setCurrentViewItems={setCurrentViewItems}
        currentTransactionForItems={currentTransactionForItems}
        setCurrentTransactionForItems={setCurrentTransactionForItems}
        carTypes={carTypes}
        loadingCarTypes={loadingCarTypes}
        deletingVehicleId={deletingVehicleId}
        deleteVehicle={deleteVehicle}
        showSnackbar={showSnackbar}
        transactions={transactions}
        setTransactions={setTransactions}
        setFilteredTransactions={setFilteredTransactions}
        isSavingVehicles={isSavingVehicles}
        setIsSavingVehicles={setIsSavingVehicles}
        itemsModalRef={itemsModalRef}
      />

      {/* Snackbar */}
      {snackbar.open && <Snackbar message={snackbar.message} type={snackbar.type} onClose={closeSnackbar} duration={5000} />}
    </AuthenticatedLayout>
  );
}

export default ExternalLoad;
